import { yupResolver } from '@hookform/resolvers/yup'
import { Close } from '@mui/icons-material'
import {
  Divider,
  Grid,
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent
} from '@mui/material'
import {
  Button,
  CardHeader,
  DatePickerC,
  IconButton,
  SelectC,
  TextFieldC,
  Tooltip
} from 'components'
import { FormProvider, useForm } from 'react-hook-form'
import {
  formatCurrency,
  formatDate,
  stringToNumber,
  useDebounce,
  Yup
} from 'utils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFutureDeliveryModalStore } from './useFutureDeliveryModalStore'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useLoadFutureDelivery } from './useLoadFutureDelivery'
import DeleteIcon from '@mui/icons-material/Delete'
import { useCreateFutureDelivery } from './useCreateFutureDelivery'
import { LengthField } from './lengthField'
import { hasLengthField } from './hasLengthField'
import { groupBy, uniqueId } from 'lodash'
import {
  useCreateParticularsComposition,
  useResolveQuantityExpression
} from 'modules/orders/hooks'
import { format } from 'date-fns'
import { createIdentifier } from '../Form/items/ItemConfiguration/services/useCreateIdentifier'

const schema = Yup.object({
  product: Yup.object({
    productId: Yup.string().required()
  }),
  date: Yup.date().required(),
  newQuantityUn: Yup.string().test(
    'is-positive',
    'deve ser maior que 0!',
    (value) => Number(value) > 0
  ),
  lengthField: Yup.mixed()
    .test(
      'is-positive',
      'deve ser maior que 0!',
      (value: any, context: any) => {
        if (hasLengthField(context?.parent?.product)) {
          const validate: any =
            stringToNumber(value?.content) || stringToNumber(value) || 0
          return validate > 0
        }
        return true
      }
    )
    .test(
      'in-range',
      'deve estar dentro da faixa de valores',
      (v: any, context: any) => {
        if (!hasLengthField(context?.parent?.product)) return true

        const isSelectField =
          context?.parent?.product?.product?.configuration?.particular?.contents
            .length
        if (isSelectField) return true

        const endRange =
          context?.parent?.product?.product?.configuration?.particular?.endRange
        const initRange =
          context?.parent?.product?.product?.configuration?.particular
            ?.initRange
        if (!!initRange && !!endRange) {
          const value = stringToNumber(v?.content) || stringToNumber(v) || 0
          return value >= initRange && value <= endRange
        }
        return true
      }
    ),
  totalQuantity: Yup.string().test(
    'have-balance',
    'verifique o saldo!',
    (value, context) => {
      // totalQuantity já é calculado por um useEffect que consulta
      // CGPR_RESOLVE_EXPRESSAO_ENG via useResolveQuantityExpression.
      const result = Number(context?.parent?.totalQuantity) || 0
      return result <= Number(context?.parent?.balance || 0)
    }
  )
})

export function FutureDeliveryModal() {
  const [rows, setRows] = useState<any>([])

  const isOpen = useFutureDeliveryModalStore.useIsModalOpen()
  const setIsOpenModal = useFutureDeliveryModalStore.useSetModalOpen()
  const orderId = useFutureDeliveryModalStore.useOrderId()

  const {
    mutateAsync: createConfiguration,
    isLoading: isSubmittingConfiguration
  } = useCreateParticularsComposition()

  const methods = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      date: null,
      newQuantityUn: '',
      product: null,
      lengthField: null,
      totalQuantity: '',
      balance: '',
      pcpObservation: ''
    }
  })

  const { handleSubmit, reset, watch, setValue, getValues } = methods

  // console.log(watch())
  // console.log(methods.formState.errors)

  const [product, newQuantityUn, lengthField] = watch([
    'product',
    'newQuantityUn',
    'lengthField'
  ])

  const { data: futureDelivery } = useLoadFutureDelivery({ orderId })

  const { mutateAsync, isLoading } = useCreateFutureDelivery()

  const onClose = () => {
    setIsOpenModal(false)
  }

  const hasParticularId = !!product?.product?.configuration?.id
  const hasQuantityRule = !!product?.quantityRule

  const groupedRows = groupBy(
    rows,
    (row: any) => `${row.productId}${row.sequence}`
  )

  const addedBalance =
    groupedRows[`${product?.productId}${product?.sequence}`]?.reduce(
      (acc, cur: any) => acc + +(cur?.totalQuantity || 0),
      0
    ) || 0

  const onSubmit = async () => {
    try {
      const normalizedRows = rows.map((row: any, index: any) => ({
        quantityUn: Number(row.newQuantityUn),
        quantity: Number(row.totalQuantity),
        productId: row.productId,
        refSequence: row.sequence,
        sequence: index + 1,
        pcpObservation: row.pcpObservation,
        identificatorId: row.identificatorId,
        deliveryDate: format(getValues('date'), 'yyyy-MM-dd'),
        lengthField:
          row?.lengthField?.content || row?.lengthField?.replace('.', ','),
        particularId: row?.product?.configuration?.particular?.id
      }))

      mutateAsync({
        orderId,
        deliveryDate: format(getValues('date'), 'yyyy-MM-dd'),
        items: normalizedRows
      })

      setIsOpenModal(false)
    } catch (err) {
      console.log(err)
    }
  }

  const onAddItem = async (item: any) => {
    const newConfigurationValues = {
      ...item.product?.configurations,
      [item.product.lengthFieldId]: item.lengthField
    }

    delete newConfigurationValues.undefined
    let response: any = {}

    if (Object.keys(newConfigurationValues).length) {
      const particularComposition = await createIdentifier(
        newConfigurationValues
      )

      response = await createConfiguration({
        particularComposition
      })
    }
    const newQuantityUn = Number(item.newQuantityUn)
    const newItem = {
      ...item,
      ...item.product,
      configurationDescription: response?.data?.description || '',
      identificatorId: response?.data?.id,
      quantityUn: newQuantityUn,
      totalPrice: item.totalQuantity * item.product.salePrice,
      id: String(item.product.productId) + String(item.product.sequence),
      uuid: uniqueId()
    }

    setRows((prev: any) => prev.concat(newItem))
    reset({
      date: item.date,
      newQuantityUn: '',
      product: null,
      lengthField: null,
      totalQuantity: '',
      balance: '',
      pcpObservation: ''
    })
  }

  const removeProduct = useCallback((id: string) => {
    setRows((prev: any) => prev.filter((product: any) => product.uuid !== id))
  }, [])

  const productList = futureDelivery?.data?.products.filter((product) => {
    const currentAdded =
      groupedRows[`${product?.productId}${product?.sequence}`]?.reduce(
        (acc, cur: any) => acc + +(cur?.totalQuantity || 0),
        0
      ) || 0

    return currentAdded < product.balance
  })

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'productId',
        headerName: 'Código',
        width: 90,
        renderCell: (params) => (
          <Tooltip title={params?.row?.configurationDescription}>
            <span>{params?.value}</span>
          </Tooltip>
        )
      },
      {
        field: 'productName',
        headerName: 'Material',
        sortable: false,
        flex: 1
      },
      {
        field: 'date',
        headerName: 'Data',
        sortable: false,
        type: 'date',
        width: 115
      },
      {
        field: 'balance',
        headerName: 'Saldo Inicial',
        sortable: false,
        type: 'number'
      },
      {
        field: 'quantityUn',
        headerName: 'Qtd. UN',
        sortable: false,
        type: 'number'
      },

      {
        field: 'totalQuantity',
        headerName: 'Qtd',
        sortable: false,
        type: 'number',
        renderCell: (params) => {
          return `${params.value} ${params?.row?.product?.unitMeasure}`
        }
      },

      {
        field: 'salePrice',
        headerName: 'Pr. Uni.',
        sortable: false,
        type: 'number',
        renderCell: (params) => formatCurrency(params.value)
      },
      {
        field: 'totalPrice',
        headerName: 'Total',
        sortable: false,
        type: 'number',
        renderCell: (params) => formatCurrency(params.value)
      },
      {
        field: 'pcpObservation',
        headerName: 'Obs.',
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={params?.value}>
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {params?.value}
            </span>
          </Tooltip>
        )
      },
      {
        field: '#',
        headerName: '#',
        width: 50,
        align: 'center',
        headerAlign: 'center',
        renderCell: (data: any) => {
          return (
            <>
              <IconButton onClick={() => removeProduct(data.row.uuid)}>
                <DeleteIcon />
              </IconButton>
            </>
          )
        }
      }
    ],
    [removeProduct]
  )

  // Monta os valores do configurador para enviar à procedure.
  // O lengthField digitado sobrescreve a posição do particular correspondente.
  const resolveFormValues = useMemo(() => {
    if (!hasQuantityRule || !hasParticularId) return {}
    const base = { ...(product?.configurations || {}) }
    if (product?.lengthFieldId) {
      base[product.lengthFieldId] =
        lengthField?.content ?? lengthField ?? base[product.lengthFieldId]
    }
    return base
  }, [
    hasQuantityRule,
    hasParticularId,
    product?.configurations,
    product?.lengthFieldId,
    lengthField
  ])

  const debouncedResolveFormValues = useDebounce(resolveFormValues, 300)
  const debouncedQuantityUn = useDebounce(Number(newQuantityUn) || 0, 300)

  const { data: resolvedData } = useResolveQuantityExpression({
    productId: product?.productId,
    configurationFormValues: debouncedResolveFormValues,
    quantityUn: debouncedQuantityUn
  })

  useEffect(() => {
    const next = hasQuantityRule
      ? resolvedData?.data?.quantity != null
        ? Number(resolvedData.data.quantity).toFixed(2)
        : null
      : newQuantityUn
    if (next == null) return
    if (getValues('totalQuantity') === next) return
    setValue('totalQuantity', next, { shouldDirty: false })
  }, [resolvedData, hasQuantityRule, newQuantityUn, getValues, setValue])

  useEffect(() => {
    const newBalance = product?.balance - addedBalance
    setValue('balance', newBalance || 0)
  }, [product, addedBalance, rows, setValue])

  useEffect(() => {
    if (!hasParticularId || !hasQuantityRule) return
    const fieldId = product?.lengthFieldId
    if (!fieldId) return
    const rawValue = product?.configurations?.[fieldId]
    if (rawValue == null || rawValue === '') return
    setValue('lengthField', rawValue)
  }, [product, hasParticularId, hasQuantityRule, setValue])

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      keepMounted={false}
      maxWidth="lg"
      scroll="paper"
    >
      <CardHeader
        title={`Entrega Futura`}
        HeaderRight={
          <IconButton onClick={() => onClose()}>
            <Close />
          </IconButton>
        }
      />
      <Divider />

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              Pedido: {futureDelivery?.data?.orderId}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              Emitido: {formatDate(futureDelivery?.data?.orderDate || '')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              Cliente: {futureDelivery?.data?.customer}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              Total do Pedido:{' '}
              {formatCurrency(futureDelivery?.data?.totalOrder)}
            </Typography>
          </Grid>

          <Grid item xs={12} container spacing={2}>
            <FormProvider {...methods}>
              <Grid item xs={12} container spacing={1} alignItems="center">
                <Grid item container xs={12}>
                  <Grid item>
                    <DatePickerC
                      name="date"
                      minDate={new Date()}
                      label={`Data`}
                      inputFormat="dd/MM/yyyy"
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={8} spacing={1}>
                  <Grid item xs={9}>
                    <SelectC
                      name="product"
                      label="Material"
                      options={productList}
                      getOptionDisabled={(option: any) => option?.balance === 0}
                      getOptionLabel={(option: any) =>
                        option?.productId +
                        ' - ' +
                        option.sequence +
                        ' - ' +
                        option?.productName
                      }
                      renderOption={(props: any, option: any) => (
                        <Box component="li" {...props}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>
                              {option?.productName}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                fontSize: 12
                              }}
                            >
                              <div>
                                {' '}
                                {option.productId} - {option.sequence}{' '}
                              </div>
                              <div style={{ marginLeft: 4, marginRight: 4 }}>
                                |
                              </div>

                              <div>Saldo Inicial: {option.balance}</div>
                            </div>
                            {option?.configurationDescription && (
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  fontSize: 12
                                }}
                              >
                                Config.: {option.configurationDescription}
                              </div>
                            )}
                          </div>
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextFieldC
                      name="balance"
                      label="Saldo"
                      disabled
                      normalize={(text) => text.replace(',', '.')}
                    />
                  </Grid>
                  {hasLengthField(product) && (
                    <Grid item xs={4}>
                      <LengthField
                        configuration={
                          product?.product.configuration.particular
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={4}>
                    <TextFieldC
                      name="newQuantityUn"
                      label="Qtd. UN"
                      normalize={(value) => value.replace(/\D/g, '')}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldC
                      name="totalQuantity"
                      label="Quantidade"
                      disabled
                      normalize={(text) => text.replace(',', '.')}
                    />
                  </Grid>
                </Grid>
                <Grid item container xs={4}>
                  <Grid item xs={9}>
                    <TextFieldC
                      name="pcpObservation"
                      label="Observação PCP"
                      rows={3}
                      multiline
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                    container
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    <Grid item>
                      <Button
                        type="button"
                        disabled={isLoading || isSubmittingConfiguration}
                        onClick={handleSubmit(onAddItem)}
                      >
                        Adicionar
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </FormProvider>
            <Grid item xs={12}>
              <Box style={{ height: 230 }}>
                <DataGrid
                  density="compact"
                  rows={rows}
                  columns={columns}
                  getRowId={({ uuid }) => uuid}
                  hideFooter
                  disableSelectionOnClick
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem'
          }}
        >
          <Button
            variant="text"
            sx={{
              width: { sm: 100, md: 200 }
            }}
            onClick={() => onClose()}
          >
            Cancelar
          </Button>
          <Button
            sx={{
              width: { sm: 100, md: 200 }
            }}
            type="button"
            disabled={isLoading || !rows.length}
            onClick={onSubmit}
          >
            Solicitar Entrega Futura
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}
